import $ from "jquery";
import EventEmitter from "eventemitter3";

export class Scenario extends EventEmitter {
  constructor(data, iframeController, notifications, skipIntroDelay = false) {
    super();

    if (data === null) {
      console.log("Data not found for CBMS, this scenario will not be initiated.");
      return;
    }

    this.machineName = data.machine_name;
    this.data = data;
    this.iframeController = iframeController;
    this.notifications = notifications;
    this.skipIntroDelay = skipIntroDelay;

    this.aborted = false;
  }

  init() {
  }

  async preload() {
    // Load the base URL (iframe is unloaded between scenarios)
    this.iframeController.loadBaseUrl();
  }

  async start() {
    this.aborted = false;

    // Wait for the base page to finish loading before anything else
    const loaded = await this.iframeController.waitForBaseLoad();
    if (this.aborted) return;
    if (!loaded) {
      console.warn('Scenario aborted: base page failed to load');
      return;
    }

    // If sitting on the login page, hold off until the user has signed in
    await this.iframeController.waitForLogin();
    if (this.aborted) return;

    this.iframeController.setBlockOverlay(true);

    // Fill in the base content
    this.notifications.clear();

    // Wait before starting
    await this.iframeController.sleep(1000);

    this.notifications.addScenarioNotification({
      title: this.data.name,
      duration: this.data.duration,
      text: this.data.long_description,
    });

    // Wait before starting
    if (!this.skipIntroDelay) {
      await this.iframeController.sleep(5000);
    }

    for (const step of this.data.steps) {
      if (this.aborted) return;

      try {
        switch (step.type) {
          case 'notification':
            await this.addNotification(step);
            break;
          case 'tooltip':
            if (step.selector_text) {
              await this.iframeController.gotoElementByTextAndWaitForClick({
                selector: step.selector,
                selectorIndex: step.selector_index ?? 0,
                textValue: step.selector_text,
                shape: step.shape ?? 'rectangle',
                message: step.text?.[app.i18n.lang] ?? null,
                padding: step.highlight_padding ?? null
              });
            } else {
              await this.iframeController.gotoSelectorAndWaitForClick({
                selector: step.selector,
                selectorIndex: step.selector_index ?? 0,
                shape: step.shape ?? 'rectangle',
                scroll: step.scroll ?? true,
                message: step.text?.[app.i18n.lang] ?? null,
                padding: step.highlight_padding ?? null
              });
            }
            break;
          case 'goto_and_auto_click':
            if (step.selector_text) {
              await this.iframeController.gotoElementByTextAndAutoClick({
                selector: step.selector,
                selectorIndex: step.selector_index ?? 0,
                textValue: step.selector_text,
                scroll: step.scroll ?? true,
                continueOnError: true,
                showHighlight: step.show_highlight ?? true
              });
            } else {
              await this.iframeController.gotoSelectorAndAutoClick({
                selector: step.selector,
                selectorIndex: step.selector_index ?? 0,
                scroll: step.scroll ?? true,
                continueOnError: true,
                showHighlight: step.show_highlight ?? true
              });
            }
            break;
          case 'select':
            await this.iframeController.gotoSelectorAndWaitForSelect({
              selectSelector: step.select_selector ?? step.selector,
              optionSelector: step.option_selector,
              selectorIndex: step.selector_index ?? 0,
              shape: step.shape ?? 'rectangle',
              scroll: step.scroll ?? true,
              continueOnError: true,
              message: step.text?.[app.i18n.lang] ?? null
            });
            break;
          case 'back':
            await this.iframeController.waitForBackClick({
              message: step.text?.[app.i18n.lang] ?? null
            });
            if (this.aborted) return;
            window.history.back();
            if (!await this.iframeController.waitForIframeLoad()) {
              console.warn("'back' step: iframe did not load within timeout");
            }
            break;
          case 'set_value':
            await this.iframeController.gotoSelectorAndWaitForValueSet({
              selector: step.selector,
              selectorIndex: step.selector_index ?? 0,
              value: step.value ?? '',
              scroll: step.scroll ?? true,
              message: step.text?.[app.i18n.lang] ?? null
            });
            break;
          case 'set_value_no_interaction':
            await this.iframeController.setSelectorValue({
              selector: step.selector,
              selectorIndex: step.selector_index ?? 0,
              value: step.value ?? '',
              scroll: step.scroll ?? true
            });
            break;
          case 'click_no_interaction':
            await this.iframeController.clickElement({
              selector: step.selector,
              selectorIndex: step.selector_index ?? 0,
              textValue: step.selector_text ?? null,
              scroll: step.scroll ?? true
            });
            break;
          case 'cutout':
            this.iframeController.showCutout({
              rectangle: step.rectangle,
              shape: step.shape ?? 'rectangle',
              message: step.text?.[app.i18n.lang] ?? null,
              padding: step.highlight_padding ?? undefined
            });
            break;
          case 'clear_cutout':
            this.iframeController.clearCutout();
            break;
          case 'cutout_and_wait':
            this.iframeController.showCutout({
              rectangle: step.rectangle,
              shape: step.shape ?? 'rectangle',
              message: step.text?.[app.i18n.lang] ?? null,
              padding: step.highlight_padding ?? undefined
            });
            await this.iframeController.waitForAnyClick();
            this.iframeController.clearCutout();
            break;
          case 'freeform':
            await this.iframeController.waitForFreeRoaming();
            break;
        }
      } catch (err) {
        console.warn(`Scenario step failed (type=${step.type}), continuing:`, err);
      }

      // Add a pause before continuing to next step and default to a 1 sec pause
      await this.iframeController.sleep(step.pause_after ?? 1000);
    }

    if (this.aborted) return;

    this.emit("completed");
  }

  reset() {
    this.aborted = true;
    this.currentStep = 0;
    this.iframeController.skip();
    this.iframeController.clearHighlight();
    this.iframeController.clearCutout();
    this.iframeController.unload();
    this.notifications.reset();
  }

  addNotification(data) {
    return this.notifications.addNotification(data);
  }
}
